import { FC, useEffect } from "react";
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form";
import { AdminObservationQuestionList } from "src/features/observationsAdmin/containers";
import useAdminForm from "src/hooks/admin/useAdminForm/useAdminForm";
import formatQuestions from "./formatQuestions";
import { Link, useNavigate } from "react-router-dom";
import { AlertModal, FormPreviewModal } from "src/modals";
import { toast } from "react-toastify";
import { CustomToast } from "src/components/CustomToast";
import { useQueryClient } from "@tanstack/react-query";
import useMutatePublishForm from "src/hooks/admin/usePublishForm/usePublishForm";
import { AlertTriangle, ArrowLeft, Eye } from "lucide-react";
import { BranchedQuestionsPreviewModal } from "src/features/Admin/Observations/components/BranchedQuestionsPreviewModal";
import ObservationPassRate from "src/features/observationsAdmin/components/ObservationPassRate/ObservationPassRate";
import { useState } from "react";
import { Button } from "src/components/ui/button";

interface Props {
	observationFormId: number;
}

const EditObservationQuestionsForm: FC<Props> = ({ observationFormId }) => {
	const { data: form } = useAdminForm(observationFormId);
	const queryClient = useQueryClient();

	const methods = useForm<AdminObservationForm>({
		defaultValues: {
			questions: [],
			selectedQuestionIndex: null,
			observationFormId,
			observationType: "INDIVIDUAL"
		},
		mode: "all",
		reValidateMode: "onBlur"
	});

	const navigate = useNavigate();

	const { control, handleSubmit } = methods;
	const { isValid } = useFormState({ control });

	const questions = useWatch({ control, name: "questions" }) ?? [];
	const observationType = useWatch({ control, name: "observationType" });

	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [isBranchedQuestionsPreviewOpen, setIsBranchedQuestionsPreviewOpen] =
		useState(false);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

	const { mutate: publishForm, isLoading: isPublishFormLoading } =
		useMutatePublishForm();

	useEffect(() => {
		if (!form) return;

		const formattedQuestions = formatQuestions(form);

		const hasObservationDateQuestion =
			formattedQuestions?.find(
				(question) => question.type === "OBSERVATION_DATE"
			) !== undefined;

		const hasCCEmailQuestion =
			formattedQuestions?.find(
				(question) => question.type === "CC_EMAIL_RECIPIENT"
			) !== undefined;

		methods.setValue("questions", formattedQuestions as any);
		methods.setValue("observationType", form.observation_type);
		methods.setValue("hasObservationDateQuestion", hasObservationDateQuestion);
		methods.setValue("hasCCEmailQuestion", hasCCEmailQuestion);

		if (form.score_threshold) {
			methods.setValue("observationScoreThreshold", form.score_threshold);
		}
	}, [form]);

	const onValid = () => {
		setIsPreviewOpen(true);
	};

	const onInvalid = () => {
		toast.error(
			<CustomToast type="Error" message="Some required fields are empty" />,
			{ hideProgressBar: true }
		);
	};

	const handlePublishFormClick = () => {
		toast.info(<CustomToast type="Info" message="Publishing Form" />);

		publishForm(
			{
				id: observationFormId,
				published: true,
				draft: false
			},
			{
				onSuccess: () => {
					toast.success(
						<CustomToast type="Success" message="Form Published successfully" />
					);

					queryClient.invalidateQueries([
						"adminDraftForms",
						"adminPublishedForms",
						"publishedForms"
					]);
					setIsConfirmationOpen(false);
					setIsPreviewOpen(false);

					navigate("/observations/forms");
				},
				onError: () => {
					toast.error(
						<CustomToast
							type="Error"
							message="There is an error publishing the form"
						/>,
						{
							autoClose: false
						}
					);
				}
			}
		);
	};

	// Only RADIO and SELECT questions can be rated for STORE_SCORED_ASSESSMENT
	const ratedQuestions = questions.filter(
		(question) => question.type === "RADIO" || question.type === "SELECT"
	);

	const hasAnsweredRatedQuestions = ratedQuestions.every(
		(question) => question.id && question.question !== ""
	);

	return (
		<>
			<FormProvider {...methods}>
				<div className="pb-8">
					<AdminObservationQuestionList
						observationFormId={observationFormId}
						onBranchedQuestionsPreviewOpen={() =>
							setIsBranchedQuestionsPreviewOpen(true)
						}
					/>

					{observationType === "STORE_SCORED_ASSESSMENT" &&
						ratedQuestions.length > 0 && (
							<div className="mt-10">
								<ObservationPassRate
									questionsLength={ratedQuestions.length}
									isDisabled={!hasAnsweredRatedQuestions}
								/>
							</div>
						)}
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<Link
						to="/observations/forms"
						className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
						Back to all forms
					</Link>

					<Button
						onClick={handleSubmit(onValid, onInvalid)}
						disabled={questions?.length === 0 || !isValid}
					>
						<Eye className="h-4 w-4" />
						Preview &amp; Publish
					</Button>
				</div>

				<BranchedQuestionsPreviewModal
					isOpen={isBranchedQuestionsPreviewOpen}
					onClose={() => setIsBranchedQuestionsPreviewOpen(false)}
				/>
			</FormProvider>

			<FormPreviewModal
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				onPublish={() => setIsConfirmationOpen(true)}
				formName={form?.name ?? "Unknown"}
				questions={questions}
				observationType={form?.observation_type}
			/>

			<AlertModal
				isOpen={isConfirmationOpen}
				onClose={() => setIsConfirmationOpen(false)}
				ModalControllers={
					<>
						<Button
							variant="outline"
							size="lg"
							onClick={() => setIsConfirmationOpen(false)}
						>
							Cancel
						</Button>
						<Button
							onClick={handlePublishFormClick}
							disabled={isPublishFormLoading}
							size="lg"
						>
							{isPublishFormLoading ? "Loading..." : "Publish"}
						</Button>
					</>
				}
			>
				<AlertTriangle size={48} data-testid="warning-icon" />
				<p>Are you sure you want to publish this form?</p>
			</AlertModal>
		</>
	);
};

export default EditObservationQuestionsForm;
